import type { ButtonProps } from '@mui/material/Button';
import { colorPalette } from '@/ui/theme/colorPalette';

const muiButtonColors = [
  'inherit',
  'primary',
  'secondary',
  'success',
  'error',
  'info',
  'warning',
] as const;

function isMuiButtonColor(color: string): boolean {
  return muiButtonColors.some((muiColor) => muiColor === color);
}

export function getResolvedPaletteColor(path: string): string | undefined {
  const [base, shade] = path.split('.');
  const group = (colorPalette as Record<string, Record<string, string>>)[base];
  if (!group) return undefined;
  return group[shade ?? 'main'];
}

export type ResolvedButtonPaletteColor = {
  isPalettePath: boolean;
  resolvedColor: string | undefined;
  muiColor: ButtonProps['color'] | undefined;
};

/**
 * Dot notation (e.g. `error.dark`) resolves from {@link colorPalette}. Bare
 * custom palette groups (e.g. `hotel`) resolve to their `main` color, while
 * built-in MUI button colors keep native theme behavior.
 */
export function resolvePaletteOrMuiButtonColor(
  color: string
): ResolvedButtonPaletteColor {
  const isPalettePath = color.includes('.') || !isMuiButtonColor(color);
  const resolvedColor = isPalettePath
    ? getResolvedPaletteColor(color)
    : undefined;
  const muiColor = isPalettePath ? undefined : (color as ButtonProps['color']);
  return { isPalettePath, resolvedColor, muiColor };
}
